import React, { useMemo, useCallback, useState } from 'react';
import clsx from 'clsx';
import { t } from 'ttag';
import {
  Typography,
  TextField,
} from '@material-ui/core';
import { Select } from '@coolremote/shared';
import Dialog from '@material-ui/core/Dialog';
import { Close } from '../../../icons';
import { useStoreActions, useStoreState } from '../../../models/RootStore';
import useStyles from './style';
import useTypes from '@hooks/useTypes';
import { Button, IconButton } from '@coolremote/shared';
import { useSetBacnetObjectValue } from '@hooks/useBacnetDevicesApi';

const EditParamPopUp = (props) => {
  const { row, onClose, unitParamTemplate, bacnetUnit } = props; 
  const classes = useStyles();
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const { bacnetUnitTypes, defaultBinaryEnums } = useTypes();

  const { mutate: setObjectValue } = useSetBacnetObjectValue();

  const { defaultReverseBinaryEnums } = useStoreState((state) => state.types);
  const [newValue, setNewValue] = useState(row?.value);

  const getParamObject = useCallback(() => {
    if (!unitParamTemplate?.data) return {};
    for (let key in unitParamTemplate.data) {
      if (unitParamTemplate.data[key].shortId === row.code.split('/')[0]) {
        return { key: key, data: unitParamTemplate.data[key] };
      }
    }
    return {};
  }, [unitParamTemplate, row]);

  const ParamObject = useMemo(() => {
    return getParamObject();
  }, [getParamObject]);

  const binaryOptions = useMemo(
    () => Object.values(defaultBinaryEnums),
    [defaultBinaryEnums]
  );

  const reverseBinaryOptions = useMemo(
    () => Object.values(defaultReverseBinaryEnums),
    [defaultReverseBinaryEnums]
  );

  const bacnetEnumerationsTemplate = useMemo(() => {
    if (!ParamObject?.data?.customEnum) {
      return [];
    }

    const { customEnum, customEnumVisibility = {} } = ParamObject.data;

    return Object.entries(customEnum)
      .map(([key, value]) => {
        if (customEnumVisibility && customEnumVisibility[key] !== false) {
          return {
            value,
            key,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);
  }, [ParamObject?.data?.customEnum]);

  const handleSave = useCallback(async () => {
    try {
      await setObjectValue({
        unitId: bacnetUnit.id,
        payload: {
          object: ParamObject.key,
          value: newValue,
        },
      });
    } catch (error) {
      addMessage({ message: error.message });
    } finally {
      onClose();
    }
  }, [ParamObject, newValue]);

  const handleValueChange = (event) => {
    setNewValue(event.target.value);
  };

  const formatValue = (val) => {
    return typeof val === 'number'
      ? Number.isInteger(val)
        ? val
        : val.toFixed(1)
      : val;
  };

  const getCustomEnumValue = (value) => {
    // Find the parameter definition by code or shortId
    const paramDef =
      unitParamTemplate?.data?.[row.code] ||
      Object.values(unitParamTemplate?.data || {}).find(
        (item) => item.shortId === row.code?.split('/')?.[0]
      );
    if (!paramDef || !paramDef.customEnum) return undefined;
    return paramDef.customEnum[value];
  };

  const hasCustomEnum = !!ParamObject.data?.customEnum;
  const min = hasCustomEnum ? Number(ParamObject.data.customEnum.min) : undefined;
  const max = hasCustomEnum ? Number(ParamObject.data.customEnum.max) : undefined;
  const step = hasCustomEnum ? Number(ParamObject.data.customEnum.step) : undefined;

  const handleNumberChange = useCallback(
    (event) => {
      let value = Number(event.target.value);
      if (!isNaN(min) && value < min) value = min;
      if (!isNaN(max) && value > max) value = max;
      setNewValue(value);
    },
    [hasCustomEnum, min, max]
  );

  const renderInputFeildType = () => (
    <>
      <span className={classes.valueTitle}>{t`Current value`}</span>
      <div className={classes.oldValueBox}>{formatValue(row.value)}<span>{row?.measurementUnits}</span></div>
      <span className={classes.valueTitle}> {t`Set value`}</span>
      <TextField
        key={'new-value'}
        fullWidth
        disabled={ParamObject.data.readOnly}
        variant="outlined"
        type={hasCustomEnum ? 'number' : 'text'}
        value={newValue}
        onChange={hasCustomEnum ? handleNumberChange : handleValueChange}
        inputProps={{
          ...(hasCustomEnum && !isNaN(min) ? { min } : {}),
          ...(hasCustomEnum && !isNaN(max) ? { max } : {}),
          ...(hasCustomEnum && !isNaN(step) ? { step } : {}),
        }}
        InputProps={{ classes: { root: classes.inputRoot } }}
      />
    </>
  );

  const renderBinaryType = (optionsList) => {
    const selectOptions = optionsList.map(({ value, label }) => ({
      value,
      label: getCustomEnumValue(value) ?? label
    }));
    return (
      <div key={'binary-type'}>
        <span className={classes.valueTitle}>{t`Current value`}</span>
        <div className={classes.oldValueBox}>{getCustomEnumValue(row.value) ?? optionsList.find(item => item.value == row.value)?.label ?? row.value}</div>

        <span className={classes.valueTitle}> {t`Set value`}</span>
        <Select
          fullWidth
          menuPortal
          value={newValue}
          onChange={setNewValue}
          options={selectOptions}
          classes={{ trigger: classes.selectInput }}
        />
      </div>
    );
  };

  const renderEnumerationType = () => {
    const selectOptions = bacnetEnumerationsTemplate.map(({ key, value }) => ({
      value: key,
      label: getCustomEnumValue(key) ?? value
    }));
    return (
      <div key={'enumerations-type'}>
        <span className={classes.valueTitle}>{t`Current value`}</span>
        <div className={classes.oldValueBox}>
          {getCustomEnumValue(row.value) ?? bacnetEnumerationsTemplate.find(item => item.key == row.value)?.value ?? row.value}
        </div>

        <span className={classes.valueTitle}> {t`Set value`}</span>
        <Select
          fullWidth
          menuPortal
          value={newValue}
          onChange={setNewValue}
          options={selectOptions}
          classes={{ trigger: classes.selectInput }}
        />
      </div>
    );
  };

  const renderAdvancedSettings = () => {
    const { binary, reverseBinary, enumeration, value, unknown } = bacnetUnitTypes || {};
    switch (ParamObject?.data?.type) {
      case binary?.value:
        return renderBinaryType(binaryOptions);
      case reverseBinary?.value:
        return renderBinaryType(reverseBinaryOptions);
      case value?.value:
      case unknown?.value:
        return renderInputFeildType();
      case enumeration?.value:
        return renderEnumerationType();
      default:
        return null;
    }
  };

  return (
    <Dialog
      key={`input-name ${row?.name}`}
      open={true}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      aria-describedby="form-dialog-description"
      maxWidth="md"
      fullWidth
      classes={{
        paper: clsx(classes.dialogStyle, { [classes.lessHeight]: row?.button }),
      }}
    >
      <div className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>{row?.name}</Typography>
        <IconButton
          disableRipple
          onClick={onClose}
          className={classes.iconBtnStyle}
        >
          <Close color="#7f7692" />
        </IconButton>
      </div>
      <div className={classes.dialogContent}>{renderAdvancedSettings()}</div>
      <div className={classes.dialogActions}>
        <Button onClick={onClose} width={130} white marginRight>
          {t`Cancel`}
        </Button>
        <Button onClick={handleSave} className={classes.resetParamsButton}>
          {t`Update`}
        </Button>
      </div>
    </Dialog>
  );
};

export default EditParamPopUp;
